import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import {
    GroupRideDeviceContract,
    GroupRideDeviceProfileContract,
    GroupRideGroupCreateContract,
    GroupRideGroupSummaryContract,
    GroupRideJoinedGroupContract,
    GroupRideJoinedGroupDeviceContract,
    GroupRideRiderLocationUpdateContract,
    GroupRideSendMessagesResponseContract,
    RiderColor,
    UserStoreService,
} from '@polaris-customerweb/sharedcomponents.core';
import {
    Vehicle,
    VehicleLocation,
} from '@polaris-customerweb/sharedcomponents.garage';
import { InProgressRideVm } from '@polaris-customerweb/sharedcomponents.rides';
import { GeoJsonPoint } from '@polaris-customerweb/sharedcomponents.shared';
import {
    UserProfile,
    UserProfileService,
} from '@polaris-customerweb/sharedcomponents.user-profile';
import {
    ActiveVehicleAlert,
    LocationAlertPath,
} from '@polaris-customerweb/sharedcomponents.vehicle-health';
import { first, last } from 'lodash-es';
import {
    combineLatest,
    EMPTY,
    map,
    Observable,
    of,
    throwError,
    timer,
} from 'rxjs';
import {
    catchError,
    exhaustMap,
    skip,
    switchMap,
    take,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    AddOfflineRideGroup,
    AddRideGroup,
    ClearAllOfflineRideGroups,
    RemoveOfflineRideGroup,
    RemoveRideGroup,
    SetOfflineRideGroups,
    SetRideGroups,
} from 'src/app/components/ride-group/ride-group.actions';
import {
    selectActiveRideGroupRider,
    selectOfflineRideGroups,
} from 'src/app/components/ride-group/ride-group.reducer';
import { ConfirmDialogComponent } from 'src/app/components/shared/components/confirm-dialog/confirm-dialog.component';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { RideGroup } from 'src/app/models/ride-group';
import { ConfirmDialogOptions } from '../components/shared/components/confirm-dialog/confirm-dialog-options';
import { InputDialogOptions } from '../components/shared/components/input-dialog/input-dialog-options';
import { InputDialogComponent } from '../components/shared/components/input-dialog/input-dialog.component';
import { GeoJsonLineString } from '../models/geojson-linestring';
import { RideGroupAlert } from '../models/ride-group-alert';
import { RideGroupInfo } from '../models/ride-group-info';
import { RideGroupMessage } from '../models/ride-group-message';
import { RideGroupRiderStatus } from '../models/ride-group-rider-status';
import { selectGroupRideMessageTime } from '../state/group-ride/group-ride-message/group-ride.selectors';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { RideGroupResource } from './ride-group-resource.service';

const GROUPS_TO_JOIN_KEY = 'groupsToJoin:';

@Injectable({ providedIn: 'root' })
export class RideGroupService {
    static rideGroupFollowMaxZoom = 15;

    readonly className = 'RideGroupService';
    private getRideGroupPositionCalledOnce = false;

    constructor(
        private configService: ConfigService,
        private matDialog: MatDialog,
        private rideGroupResource: RideGroupResource,
        private store: Store<AppState>,
        private userAuthService: UserAuthService,
        private userProfileService: UserProfileService,
        private userStoreService: UserStoreService
    ) {
        // get the groups from local storage
        // set the groups or []
        const storedGroups = this.userStoreService.get(GROUPS_TO_JOIN_KEY) as
            | RideGroup[]
            | null;
        const localGroups = storedGroups?.map(
            (group) => new RideGroup(group.toContract())
        );

        this.store.dispatch(new SetOfflineRideGroups(localGroups || []));

        userAuthService.whenAuthenticated$
            .pipe(switchMap(() => this.getMyGroups()))
            .subscribe((groups) => {
                this.store.dispatch(
                    new SetRideGroups(
                        groups.map((group) => new RideGroup(group))
                    )
                );
            });

        // subscribe to the offlineRideGroups
        this.store
            .pipe(select(selectOfflineRideGroups), skip(1))
            .subscribe((groups) =>
                this.userStoreService.set(GROUPS_TO_JOIN_KEY, groups)
            );
    }

    //#region Groups
    getByLocation(
        lat: number,
        lng: number,
        distanceMeters?: number
    ): Observable<GroupRideGroupSummaryContract[]> {
        return this.rideGroupResource.getByLocation(lat, lng, distanceMeters);
    }

    getById(id: string): Observable<RideGroup> {
        return this.rideGroupResource.getById(id);
    }

    getByInviteId(id: string): Observable<RideGroup> {
        return this.rideGroupResource.getByInviteId(id);
    }

    pollRideGroupInfo$(rideGroupId: string): Observable<RideGroupInfo> {
        return timer(
            0,
            this.configService.pollTimers.rideGroupInfo * 1000
        ).pipe(
            switchMap(() => {
                if (this.getRideGroupPositionCalledOnce) {
                    this.getRideGroupPositionCalledOnce = false;
                    return EMPTY;
                }

                return this.getRideGroupInfo(rideGroupId);
            })
        );
    }

    getRideGroupInfoOnce(rideGroupId: string): Observable<RideGroupInfo> {
        this.getRideGroupPositionCalledOnce = true;
        return this.updateRideGroupInfo(rideGroupId);
    }

    /**
     * Get groups the current user has joined.
     * @param minutesSinceActive The minutes since active for calculating the active device/rider counts (default is 1 minute).
     * @returns Observable of array of groups.
     */
    getMyGroups(
        minutesSinceActive?: number | null
    ): Observable<GroupRideJoinedGroupContract[]> {
        return this.rideGroupResource.getMyGroups(minutesSinceActive);
    }
    //#endregion

    //#region Get status items
    getRideGroupStatusItems(
        groupId: string
    ): Observable<GroupRideRiderLocationUpdateContract[]> {
        return this.rideGroupResource.getRideGroupStatusItems(groupId);
    }

    pollRideGroupStatusItems$(
        groupId: string,
        pollMs = this.configService.pollTimers.groupRideStatusItems * 1000
    ): Observable<GroupRideRiderLocationUpdateContract[]> {
        return timer(0, pollMs).pipe(
            exhaustMap(() => this.getRideGroupStatusItems(groupId))
        );
    }
    //#region

    //#region Create
    createRideGroup(
        name: string,
        id: string | undefined,
        isPrivate: boolean,
        passcode: string
    ): Observable<RideGroup> {
        const contract: GroupRideGroupCreateContract = {};
        contract.name = name;
        contract.passcode = passcode;
        contract.isPrivate = isPrivate;
        contract.id = id;

        return this.rideGroupResource.createGroup(contract).pipe(
            switchMap((newGroupId) => {
                return this.rideGroupResource.getById(newGroupId.id).pipe(
                    tap((group) => {
                        this.addToGroups(group);
                    })
                );
            })
        );
    }
    //#endregion

    //#region Leave/join group
    leaveGroup(id: string): Observable<void> {
        return this.rideGroupResource.leaveGroup(id);
    }

    // i18n text is in the ride-group-card.component.html
    promptAndLeaveGroup(rideGroup: RideGroup): Observable<boolean> {
        const data = new ConfirmDialogOptions({
            cancelButtonId: 'cancelLeaveGroup',
            confirmButtonId: 'confirmLeaveGroup',
            confirmText: $localize`Leave`,
            contentText: $localize`Are you sure you want to leave this group?`,
            dialogId: 'leaveGroup',
            hideCancelButton: false,
            titleText: $localize`Leave Group`,
        });
        const confirm = this.matDialog.open(ConfirmDialogComponent, { data });
        return confirm.afterClosed().pipe(
            switchMap((result) => {
                if (result === 'confirm') {
                    return this.leaveGroup(rideGroup.groupId).pipe(
                        switchMap(() => {
                            this.addToOfflineGroups(rideGroup);
                            this.removeFromGroups(rideGroup);
                            return of(true);
                        })
                    );
                }
                return of(false);
            })
        );
    }

    joinGroupByInvite(inviteId: string): Observable<RideGroup> {
        if (!inviteId) {
            return throwError(() => 'no invite id');
        }

        return this.userAuthService.isAuthenticated$.pipe(
            take(1),
            switchMap((isAuthenticated) => {
                if (!isAuthenticated) {
                    return throwError(() => 'not authenticated');
                }

                return this.promptAndJoinGroupByInviteId(inviteId);
            })
        );
    }

    promptAndJoinGroupByInviteId(inviteId: string): Observable<RideGroup> {
        return this.getByInviteId(inviteId).pipe(
            switchMap((group) => {
                if (group.isPrivate) {
                    return this.showPasscodeDialogAndJoinGroup(group);
                } else {
                    return this.joinGroup(group, undefined, inviteId);
                }
            })
        );
    }

    promptAndJoinGroupByGroupId(groupId: string): Observable<RideGroup> {
        return this.getById(groupId).pipe(
            switchMap((group) => {
                if (group.isPrivate) {
                    return this.showPasscodeDialogAndJoinGroup(group);
                } else {
                    return this.joinGroup(group);
                }
            })
        );
    }
    //#endregion

    //#region Offline list
    clearOfflineList(): void {
        this.store.dispatch(new ClearAllOfflineRideGroups());
    }

    getRideGroupsFromOfflineList(): Observable<RideGroup[]> {
        return this.store.pipe(select(selectOfflineRideGroups));
    }

    removeFromOfflineGroups(rideGroup: RideGroup): void {
        this.store.dispatch(new RemoveOfflineRideGroup(rideGroup));
    }
    //#endregion

    //#region GeoJSON
    rideGroupRidersAndVehicleLocationToGeoJson(
        activeAlerts: ActiveVehicleAlert[],
        deviceProfiles: GroupRideDeviceProfileContract[],
        inProgressRides: readonly InProgressRideVm[],
        rideGroupRiders: RideGroupRiderStatus[],
        leader: RideGroupRiderStatus,
        userProfile: UserProfile,
        vehicleLocations: readonly VehicleLocation[],
        vehicles: readonly Vehicle[],
        locationAlertPaths: readonly LocationAlertPath[]
    ): GeoJSON.FeatureCollection {
        const riderPositions = rideGroupRiders
            ? rideGroupRiders.map((rideGroupRider) =>
                  rideGroupRider.toGeoJson()
              )
            : [];

        const leaderLines: GeoJSON.Feature[] =
            leader?.leaderLine && leader.leaderLineGeoJson
                ? [leader.leaderLineGeoJson]
                : [];

        const locationAlertGeoJson = locationAlertPaths.map((alertPath) => {
            const vehicleWithAlert = vehicles.find(
                (vehicle) => vehicle.vin === alertPath.vin
            );
            return new GeoJsonLineString(alertPath.path, {
                activeLocationAlert: true,
                id: vehicleWithAlert?.id || '',
            });
        });

        const locations: Record<
            string,
            GeoJSON.Feature<GeoJSON.Point> | undefined
        > = vehicleLocations.reduce((result, location) => {
            const locationGeoJson = location.toGeoJson();
            const vehicle = vehicles.find(
                (veh) => veh.id === location.vehicleId
            );
            const alert = activeAlerts.find((ale) => ale.vin === location.vin);
            const inProgressRide = inProgressRides.find(
                (ride) => ride.vin === location.vin
            );

            const deviceColor = this.getDeviceColorForVin(
                deviceProfiles,
                userProfile,
                location.vin
            );
            locationGeoJson.properties = locationGeoJson.properties || {};
            locationGeoJson.properties.profileColor = deviceColor;

            if (
                this.configService.featureFlags.areLocationAlertsEnabled &&
                vehicle?.bumpAlertDisabled !== null &&
                vehicle?.theftAlertDisabled !== null &&
                (!vehicle?.bumpAlertDisabled || !vehicle.theftAlertDisabled)
            ) {
                locationGeoJson.properties.locationAlertsEnabled = true;
            }
            if (inProgressRide?.coordinates?.length) {
                locationGeoJson.geometry.coordinates = [
                    inProgressRide.coordinates[
                        inProgressRide.coordinates.length - 1
                    ].longitude,
                    inProgressRide.coordinates[
                        inProgressRide.coordinates.length - 1
                    ].latitude,
                ];
                locationGeoJson.properties.profileColor = inProgressRide.color;
                locationGeoJson.properties.bearing = inProgressRide.bearing;
            }
            if (alert) {
                locationGeoJson.properties.activeLocationAlert = true;
                const locationAlertPath = locationAlertPaths.find(
                    (path) => path.vin === vehicle?.vin
                );
                if (locationAlertPath) {
                    locationGeoJson.geometry.coordinates = last(
                        locationAlertPath.path
                    ) as number[];
                    locationGeoJson.properties.bearing =
                        locationAlertPath.bearing;
                }
            }
            result[location.vin] = locationGeoJson;
            return result;
        }, {});

        for (const alertPath of locationAlertPaths) {
            const location = vehicleLocations.find(
                (loc) => loc.vin === alertPath.vin
            );
            if (!location) {
                const pathPoint = first(alertPath.path);
                const profileColor = this.getDeviceColorForVin(
                    deviceProfiles,
                    userProfile,
                    alertPath.vin
                );
                locations[alertPath.vin] = new GeoJsonPoint(
                    pathPoint?.[1],
                    pathPoint?.[0],
                    { activeLocationAlert: true, profileColor }
                );
            }
        }

        const groupRideList = riderPositions.reduce<GeoJSON.Feature[]>(
            (result, riderPosition) => {
                riderPosition.properties ??= {};
                const location =
                    locations[riderPosition.properties.deviceId as string];
                if (location) {
                    location.properties ??= {};
                    result.push({
                        ...riderPosition,
                        properties: {
                            ...riderPosition.properties,
                            connectedGroupride: true,
                            type: location.properties.type as string,
                            vehicleId: location.properties.id as string,
                        },
                    });
                    locations[riderPosition.properties.deviceId as string] =
                        undefined;
                } else {
                    result.push(riderPosition);
                }
                return result;
            },
            []
        );

        const locationList = Object.values(locations).reduce<GeoJSON.Feature[]>(
            (result, value) => {
                if (value) {
                    value.properties ??= {};
                    //below change is temporary, until GIS removes old connected layers.
                    value.properties.connectedGroupride = true;
                    result.push(value);
                }
                return result;
            },
            []
        );

        const geoJson = new GeoJsonFeatureCollection([
            ...groupRideList,
            ...leaderLines,
            ...locationList,
            ...locationAlertGeoJson,
        ]);

        return geoJson;
    }
    //#endregion

    //#region Messages
    getNewAlertMessages(
        previousAlerts: RideGroupAlert[],
        newAlerts: RideGroupAlert[],
        riders: RideGroupRiderStatus[]
    ): RideGroupMessage[] {
        const newMsgs: RideGroupMessage[] = [];
        // create new alert messages for newly alerted riders
        for (const newAlert of newAlerts) {
            if (
                !previousAlerts.find(
                    (x) => x.riderFeatureId === newAlert.riderFeatureId
                )
            ) {
                newMsgs.push(
                    RideGroupMessage.fromAlert(
                        newAlert,
                        riders.find((x) => x.riderId === newAlert.riderId)
                    )
                );
            }
        }
        // create canceled alert messages for newly out of alert riders
        for (const previousAlert of previousAlerts) {
            if (
                !newAlerts.find(
                    (x) => x.riderFeatureId === previousAlert.riderFeatureId
                )
            ) {
                const newMsg = RideGroupMessage.fromAlert(
                    previousAlert,
                    riders.find((x) => x.riderId === previousAlert.riderId),
                    true
                );
                newMsg.isCanceled = true;
                newMsgs.push(newMsg);
            }
        }
        return newMsgs;
    }

    dedupeMessages(
        existingMessages: RideGroupMessage[],
        newMessages: RideGroupMessage[]
    ): RideGroupMessage[] {
        const messages = existingMessages;
        if (newMessages?.length)
            for (const message of newMessages) {
                const minSentAtTime = message.sentAt - 300000000;
                // if there doesn't exist a message with
                //    same sender
                //    and sent within 30 seconds of this message
                //    and at least one of the messages was relayed
                //    and text matches
                // we can add it to the message list
                // logic will not catch every duplicate message but it matches IVI and app logic
                if (
                    !messages.some(
                        (msg) =>
                            msg.fromSenderId === message.fromSenderId &&
                            msg.sentAt >= minSentAtTime &&
                            (msg.relayedBySenderId ||
                                message.relayedBySenderId) &&
                            msg.message.length === message.message.length &&
                            msg.message.every(
                                (m, i) => m === message.message[i]
                            )
                    )
                ) {
                    messages.push(message);
                }
            }
        return messages;
    }

    sendMessage(
        groupId: string,
        message: string
    ): Observable<GroupRideSendMessagesResponseContract> {
        return this.rideGroupResource.sendMessage(groupId, message);
    }
    //#endregion

    //#region Devices
    getRideGroupDevices(
        reportedSince?: Date
    ): Observable<GroupRideDeviceContract[]> {
        const withinAnHour = new Date();
        withinAnHour.setHours(withinAnHour.getHours() - 1);

        const updatedReportedSince = reportedSince
            ? reportedSince
            : withinAnHour;

        return this.rideGroupResource.getGroupRideDevices(updatedReportedSince);
    }

    getDeviceProfile(
        deviceId: string
    ): Observable<GroupRideDeviceProfileContract> {
        return this.rideGroupResource.getDeviceProfile(deviceId);
    }

    getDeviceProfiles(): Observable<GroupRideDeviceProfileContract[]> {
        return this.rideGroupResource.getDeviceProfiles();
    }

    getDeviceColorForVin(
        deviceProfiles: GroupRideDeviceProfileContract[],
        userProfile: UserProfile,
        vin: string
    ): string {
        const deviceProfile = deviceProfiles.find(
            (profile) => profile.deviceId === vin
        );

        if (deviceProfile?.color) {
            return deviceProfile.color;
        } else if (userProfile?.riderColor) {
            return userProfile.riderColor;
        } else {
            let profileColor: RiderColor = 'Blue';
            if (this.configService.isOffRoad) {
                profileColor = 'Blue';
            } else if (this.configService.isIndian) {
                profileColor = 'Red';
            } else if (this.configService.isSlingshot) {
                profileColor = 'Orange';
            }
            return profileColor;
        }
    }

    updateDeviceSharing(
        groups: GroupRideJoinedGroupDeviceContract[]
    ): Observable<GroupRideJoinedGroupDeviceContract[]> {
        return this.rideGroupResource.updateDeviceSharing(groups).pipe(take(1));
    }
    //#endregion

    private getRideGroupInfo(rideGroupId: string): Observable<RideGroupInfo> {
        return this.store.select(selectGroupRideMessageTime).pipe(
            switchMap((time) =>
                this.rideGroupResource.getRideGroupStatus(rideGroupId, time)
            ),
            withLatestFrom(this.userProfileService.getUserProfile$()),
            map(
                ([contract, profile]) =>
                    new RideGroupInfo(
                        contract,
                        profile?.riderId ?? '',
                        this.configService.pollTimers.groupRideMaxInactivePeriod
                    )
            )
        );
    }

    private updateRideGroupInfo(
        rideGroupId: string
    ): Observable<RideGroupInfo> {
        return combineLatest([
            this.store.pipe(select(selectActiveRideGroupRider), take(1)),
            this.store.select(selectGroupRideMessageTime),
        ]).pipe(
            switchMap(([rider, time]) =>
                this.rideGroupResource.postRiderStatus(rideGroupId, rider, time)
            ),
            withLatestFrom(this.userProfileService.getUserProfile$()),
            map(
                ([contract, profile]) =>
                    new RideGroupInfo(
                        contract,
                        profile?.riderId ?? '',
                        this.configService.pollTimers.groupRideMaxInactivePeriod
                    )
            )
        );
    }

    // i18n text is in the ride-group-card.component.html
    private showPasscodeDialogAndJoinGroup(
        group: RideGroup
    ): Observable<RideGroup> {
        const data = new InputDialogOptions({
            confirmText: $localize`OK`,
            contentText: $localize`Passcode`,
            dialogId: 'passcode',
            hideCancelButton: false,
            titleText: $localize`Enter Passcode`,
        });
        const confirm = this.matDialog.open<
            InputDialogComponent,
            InputDialogOptions,
            string
        >(InputDialogComponent, {
            data,
        });

        return confirm.afterClosed().pipe(
            switchMap((result) => {
                if (result === 'cancel') {
                    this.addToOfflineGroups(group);
                    return throwError(() => 'passcode cancelled');
                } else {
                    // attempt user to group
                    return this.joinGroup(group, result);
                }
            })
        );
    }

    private joinGroup(
        group: RideGroup,
        passcode?: string,
        inviteId?: string
    ): Observable<RideGroup> {
        return this.rideGroupResource.joinGroup(group, passcode, inviteId).pipe(
            tap((joinedGroup) => {
                this.removeFromOfflineGroups(joinedGroup);
                this.addToGroups(joinedGroup);
            }),
            catchError((err: HttpErrorResponse) => {
                if (err.status === 401) {
                    this.addToOfflineGroups(group);
                }
                return throwError(() => err);
            })
        );
    }

    private addToOfflineGroups(rideGroup: RideGroup): void {
        this.store.dispatch(new AddOfflineRideGroup(rideGroup));
    }

    private addToGroups(rideGroup: RideGroup): void {
        this.store.dispatch(new AddRideGroup(rideGroup));
    }

    private removeFromGroups(rideGroup: RideGroup): void {
        this.store.dispatch(new RemoveRideGroup(rideGroup));
    }
}
